/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @typedef {Object} MultivariateInfo
 * @description an object that defines information about a multivariate (optional factor) used in forecasting
 * @property {String} columnId - the columnId of the column
 * @property {String} label - the label of the column which either corresponds to metadataColumn.getLabel() or missing column labels (Missing:columnId)
 * @property {Boolean} isMissing - true in the case where the entry is not in the metadata and the missing column label will be rendered.
 */

/**
 * @class ForecastAPI
 * @hideconstructor
 * @classdesc
 */
define([], () => {
	class ForecastAPI {
		/**
		 * @function ForecastAPI#isEnabled
		 * @description Determines whether forecast is enabled for the current content, regardless of the state.
		 * @returns {boolean} true if forecast is enabled for the content, otherwise false
		 */
		isForecastEnabled() {}

		/**
		* @returns {Array} an array of forecast settings that are currently
		* in the model. Will return {} if the toggle switch is off.
		*/
		getForecastSettings() {}

		/**
		* @returns {Boolean} true iff there are any valid forecast data
		*/
		areThereValidForecastSettings(/* options */) {}

		/**
		* Clear both the SA forecasting messages
		*/
		clearForecastIndicatorMessages() {}

		/**
		* @returns {Object} the current forecasting state in the form easily rendered in a dialog
		* Format:
		* {
		*	toggleEnabled: {Boolean} true iff the toggleSwitch is on (i.e. forecasting active)
		*	settings: {Array} array containing settings objects
		* }
		*/
		getForecastDisplayState() {}

		/**
		* @returns {Object} an object of forecast info provided by predict plus the
		* forecast settings that are currently enabled in the model. Will return {} if
		* the toggle switch is off.
		*/
		getForecastInfo() {}

		/**
		* Update the model with the new forecasting settings state (i.e. is a forecast
		* considered enabled or not).
		* @param {Object} new forecasting state
		*	{
		*		toggleEnabled: {Boolean} true iff the forecast is current selected (i.e. on)
		*		id: {String} ID used to store the forecast when selected in the spec.
		*	}
		*/
		applyStateChange(/* newState */) { }

		/**
		* @returns {Promise} which is resolved when we have a valid forecasting response
		* The response will be of the format:
		*	{
		*		{Object} key/value pairs of forecast settings
		*	}
		*/
		whenForecastSuggestionsReady(/* suggestionResponse, viewSupportsForecasts */) { }

		/**
		* Set query result
		* @param {QueryResultsAPI} Query result forecast data
		*/
		setQueryResult(/* queryResult */) { }

		/**
		* Get query result
		* @returns {QueryResultsAPI} Query result forecast data
		*/
		getQueryResult() { }

		/**
		* Returns whether current vis supports forecast or not
		* @returns {Boolean} true iff viz supports forecast
		*/
		supportsForecasts () {}

		/**
		* Modifies the query spec pre query excecution
		* @param {Object} querySpec Query spec
		* @param {Boolean} isExecution Indicates if the query is a smart execution query
		*/
		preQueryExecution(/* querySpec, isExecution */) { }

		/**
		* Modifies the query response
		* @param {Object} payload Query response payload
		* @param {Object} predictResults Predict result from the query response
		* @param {Boolean} isExecution Indicates if the query is a smart execution query
		*/
		postQueryExecution(/* payload, predictResults, isExecution, */) { }

		/**
		 * Determine if there is any forecast data to render.
		 * @return true iff there are forecast annotations to render
		 */
		hasForecastData() {}

		/**
		* Remove specified listener from receiving message updates
		* @param {Object} messageListener - ignored for now but included in case we
		* want to expand the manager to later allow multiple listeners
		*/
		removeMessageListener(/*messageListener*/) { }

		/**
		* Add a specified listener who will be told when SA messages have been updated.
		* @param {Object} listener who can respond to setForecastMessages
		*/
		addMessageListener(/* messageListener */) { }

		/*
		* Checks for statistical details
		* @returns {Boolean} true if the query results has statistical details
		*/
		hasStatisticalDetails() { }

		/**
		* @returns {Array} the current forecasting multivariate state array of objects as persisted in the model
		* Format:
		* {
		*	name: 'columnIdLabel',	//TODO: we should consider removing this from the model.
		*   columnId: 'datasource.columnId'
		* }
		*/
		getMultivariatesState() { }

		/**
		 * @public
		 * @function ForecastAPI#getMultivariatesInfoList
		 * @description Return UI-friendly information about each multivariate (optional factor).
		 * @returns {MultivariateInfo[]} An array of MultivariateInfo representing the columns, their labels and missing status.
		 */
		getMultivariatesInfoList() { }
	}
	return ForecastAPI;
});
